import React, { useMemo, useCallback, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import toLower from 'lodash/toLower'

import { useDialog } from '@galo-monitor/dialog'
import { useT } from '@galo-monitor/i18n'
import useGaloMonitorApi from '@galo-monitor/services/hooks/useGaloMonitorApi'
import { useSnackbar } from '@galo-monitor/snackbar'
import { PropertyActions } from '@galo-monitor/stores/property'
import { selectCurrentProperty } from '@galo-monitor/stores/property/selectorProperty'
import DataTable from '@galo-monitor/web-components/DataTable'
import ConfirmationModal from '@galo-monitor/web-containers/modals/ConfirmationModal'
import ErrorModal from '@galo-monitor/web-containers/modals/ErrorModal'
import ImportMapModal from '@galo-monitor/web-containers/modals/farm/ImportMapModal'
import MapModal from '@galo-monitor/web-containers/modals/farm/MapModal'
import { useRoutes } from '@galo-monitor/web-containers/routes/authenticated'

const PropertyOverviewMap = () => {
  const t = useT()
  const snackbar = useSnackbar()
  const { createDialog } = useDialog()
  const dispatch = useCallback(useDispatch(), [])
  const { routes, getActionsBySlug } = useRoutes()

  const currentProperty = useSelector(selectCurrentProperty)

  const { data: plots, mutate, isValidating } = useGaloMonitorApi('/farms/properties/maps')

  const userActions = useMemo(
    () => getActionsBySlug(routes.maps.slug),
    [getActionsBySlug, routes.maps.slug]
  )

  const columns = useMemo(
    () => [
      {
        title: t('name'),
        field: 'name'
      },
      {
        title: `${ t('area', { howMany: 1 }) } (ha)`,
        field: 'area'
      }
    ],
    [t]
  )

  const onAddClick = useCallback(
    () => {
      createDialog({
        id: 'create-overview-map',
        Component: MapModal,
        props: {
          onSuccess: mutate
        }
      })
    },
    [createDialog, mutate]
  )

  const onEditClick = useCallback(
    (_, mapObject) => {
      createDialog({
        id: 'edit-overview-map',
        Component: MapModal,
        props: {
          mapObject,
          onSuccess: mutate
        }
      })
    },
    [createDialog, mutate]
  )

  const onDeleteClick = useCallback(
    (_, plot) => {
      createDialog({
        id: 'confirmation',
        Component: ConfirmationModal,
        props: {
          title: t('are you sure you want to delete the "{name}" {this}?', {
            gender: 'male',
            howMany: 1,
            this: t('map', { howMany: 1 }),
            name: plot.name
          }),
          onConfirm: () => {
            dispatch(PropertyActions.removePropertyMap(
              plot,
              () => {
                snackbar.success(t('{this} {action} successfully!', {
                  this: t('map', { howMany: 1 }),
                  action: toLower(t('removed', { gender: 'male', howMany: 1 }))
                }))
                mutate()
              },
              () => {
                createDialog({
                  id: 'plots-error',
                  Component: ErrorModal,
                  props: {
                    title: `${ t('could not {action} the {this}', {
                      howMany: 1,
                      gender: 'male',
                      action: toLower(t('remove')),
                      this: toLower(t('map', { howMany: 1 }))
                    })  }.`
                  }
                })
              }
            ))
          }
        }
      })
    },
    [createDialog, dispatch, mutate, snackbar, t]
  )

  const onImportClick = useCallback(
    () => {
      createDialog({
        id: 'import-overview-map',
        Component: ImportMapModal,
        props: {
          onSuccess: mapData => createDialog({
            id: 'create-overview-map',
            Component: MapModal,
            props: {
              mapObject: mapData,
              onSuccess: mutate
            }
          })
        }
      })
    },
    [createDialog, mutate]
  )

  useEffect(() => {
    mutate()
  }, [currentProperty, mutate])

  return (
    <DataTable
      loading={ isValidating }
      data={ plots || [] }
      columns={ columns }
      onAddClick={ userActions.write && onAddClick }
      onMultipleActionsPopover={ [
        {
          text: t('create {this}', { this: t('map', { howMany: 1 }) }),
          onClick: onAddClick
        },
        {
          text: t('import {this}', { this: t('map', { howMany: 1 }) }),
          onClick: onImportClick
        }
      ] }
      onEditClick={ userActions.edit && onEditClick }
      onDeleteClick={ userActions.delete && onDeleteClick }
    />
  )
}

export default PropertyOverviewMap
